import { addCivilDays, buildHolidayMap, toCivilDate } from '../day-helpers.js';
import { buildObservedHolidays, classifyDay } from '../metadata-helpers.js';
import type {
  Holiday,
  ResolveProceduralDateInput,
  ResolveProceduralDateResult,
  SkippedDate,
} from '../types.js';

/**
 * US Federal business days: walk the calendar one day at a time from the
 * anchor, counting only Monday–Friday that aren't federal holidays. The anchor
 * itself is day 0 and the result always lands on a business day by
 * construction (no roll-over needed). Only `days` is honoured; `years` /
 * `months` are ignored with a warning.
 */
export function resolveUsFederalBusiness(
  input: ResolveProceduralDateInput,
): ResolveProceduralDateResult {
  const start = toCivilDate(input.startDate);
  const { years = 0, months = 0, days = 0 } = input.interval;
  const holidayMap = buildHolidayMap(input.holidays);

  const warnings: string[] = [];
  if (years !== 0 || months !== 0) {
    warnings.push('Years/months on the interval are ignored for US Federal business-day counting.');
  }

  if (days === 0) {
    return shape(start, start, [], [], holidayMap, warnings);
  }

  const step = days > 0 ? 1 : -1;
  const target = Math.abs(days);

  const countedDates: Date[] = [];
  const skippedDates: SkippedDate[] = [];

  let cursor = addCivilDays(start, step);
  let counted = 0;
  while (counted < target) {
    const { isCounting, skip } = classifyDay(cursor, holidayMap);
    if (isCounting) {
      countedDates.push(cursor);
      counted += 1;
    } else if (skip !== undefined) {
      skippedDates.push(skip);
    }
    if (counted < target) cursor = addCivilDays(cursor, step);
  }

  return shape(start, cursor, countedDates, skippedDates, holidayMap, warnings);
}

function shape(
  start: Date,
  resultDate: Date,
  countedDates: Date[],
  skippedDates: SkippedDate[],
  holidayMap: Map<string, Holiday>,
  warnings: string[],
): ResolveProceduralDateResult {
  const windowStart = resultDate.getTime() < start.getTime() ? resultDate : start;
  const windowEnd = resultDate.getTime() < start.getTime() ? start : resultDate;

  return {
    resultDate,
    jurisdiction: 'us-federal',
    dayCount: 'business',
    direction:
      resultDate.getTime() === start.getTime()
        ? 'same-day'
        : resultDate.getTime() > start.getTime()
          ? 'after'
          : 'before',
    startDateExcluded: false,
    endDateRolledForward: false,
    weekendsCount: false,
    publicHolidaysCount: false,
    countedDates,
    skippedDates,
    observedHolidays: buildObservedHolidays(holidayMap, windowStart, windowEnd),
    windowStart,
    windowEnd,
    warnings,
  };
}
